import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin, ArrowRight, Heart, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { FadeUp, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";

import { forwardRef } from "react";

const Footer = forwardRef<HTMLElement>((_, ref) => (
  <footer ref={ref} className="bg-secondary border-t border-border relative overflow-hidden" role="contentinfo">
    {/* Top accent */}
    <div className="glow-line" />
    
    {/* Ambient orbs */}
    <div className="orb-glow orb-gold w-[600px] h-[600px] -top-80 left-1/4 opacity-6" />
    <div className="orb-glow orb-gold w-[400px] h-[400px] -bottom-40 right-1/4 opacity-4" />

    <div className="container-luxury section-padding relative">
      {/* Newsletter banner */}
      <FadeUp className="mb-20">
        <div className="luxury-card-elite rounded-2xl p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 mesh-gradient" />
          <div className="absolute inset-0 bg-diagonal opacity-10" />
          <div className="relative">
            <motion.div
              className="w-14 h-14 rounded-2xl gold-gradient-bg flex items-center justify-center mx-auto mb-6 gold-glow"
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              <Sparkles size={24} className="text-primary-foreground" />
            </motion.div>
            <h3 className="font-display text-2xl md:text-4xl font-bold mb-4 leading-tight">
              Ready for a <span className="gold-gradient-text-animated">Showroom Finish</span>?
            </h3>
            <p className="text-foreground/90 text-sm mb-8 max-w-md mx-auto leading-relaxed">
              Book your premium mobile detail today. We come to you in DC, Maryland & Virginia.
            </p>
            <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/book"
                className="inline-flex items-center gap-2 gold-gradient-bg text-primary-foreground px-8 py-3.5 text-sm font-semibold tracking-wider rounded-xl btn-luxury btn-shine btn-magnetic"
              >
                Book Now <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </FadeUp>

      <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-10">
        <StaggerItem>
          <div>
            <Link to="/" className="inline-flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-lg gold-gradient-bg flex items-center justify-center">
                <Sparkles size={14} className="text-primary-foreground" />
              </div>
              <h3 className="font-display text-xl font-bold gold-gradient-text-animated">METROSHINE</h3>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Premium mobile car detailing serving Washington DC, Maryland, and Northern Virginia. We bring luxury detailing to your driveway.
            </p>

            {/* Review badges */}
            <div className="flex flex-col gap-2 mb-6">
              <a
                href="https://maps.google.com?q=MetroShine&ftid=0x20aa3a091ea792f:0x613f85e03407b5a1&entry=gps"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 glass-ultra rounded-xl px-3.5 py-2.5 text-xs hover:border-primary/40 transition-all group w-fit"
                aria-label="See our 18 reviews on Google"
              >
                <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.3-.1-2.3-.4-3.5z"/></svg>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-foreground">Google</span>
                  <span className="text-primary">★ 5.0</span>
                  <span className="text-muted-foreground">(18)</span>
                </div>
              </a>
              <a
                href="https://www.yelp.com/biz/metroshine-vienna"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 glass-ultra rounded-xl px-3.5 py-2.5 text-xs hover:border-primary/40 transition-all w-fit"
                aria-label="See our Yelp page"
              >
                <span className="font-bold text-[#d32323] text-sm tracking-tight">yelp</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-primary">★ 5.0</span>
                  <span className="text-muted-foreground">(1)</span>
                </div>
              </a>
            </div>

            <div className="flex gap-3">
              {[
                { href: "https://www.instagram.com/themetroshine/", icon: Instagram, label: "Instagram" },
                { href: "mailto:themetroshine@gmail.com", icon: Mail, label: "Email" },
                { href: "tel:+18446426247", icon: Phone, label: "Call" },
              ].map(({ href, icon: Icon, label }) => (
                <motion.a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="w-10 h-10 rounded-xl glass-ultra flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all duration-300"
                  aria-label={label}
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon size={17} />
                </motion.a>
              ))}
            </div>
          </div>
        </StaggerItem>

        <StaggerItem>
          <nav aria-label="Services">
            <h4 className="floating-label mb-6">Services</h4>
            <div className="flex flex-col gap-3.5">
              {[
                { to: "/services/full-detail", label: "Full Detail" },
                { to: "/services/interior-detail", label: "Interior Detail" },
                { to: "/services/exterior-detail", label: "Exterior Detail" },
                { to: "/services/paint-correction", label: "Paint Correction" },
                { to: "/services/ceramic-sealant", label: "Ceramic Sealant" },
                { to: "/services/ceramic-coating-1-year", label: "1-Yr Ceramic Coating" },
                { to: "/services/ceramic-coating-2-3-year", label: "2–3 Yr Ceramic Coating" },
                { to: "/services/ceramic-coating-5-year", label: "5-Yr Ceramic Coating" },
                { to: "/services/hand-wash", label: "Mobile Hand Wash" },
                { to: "/pricing", label: "Pricing & Packages" },
              ].map((link) => (
                <Link key={link.label} to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors link-underline w-fit inline-flex items-center gap-1.5 group">
                  {link.label}
                  <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </nav>
        </StaggerItem>

        <StaggerItem>
          <nav aria-label="Commercial & Company">
            <h4 className="floating-label mb-6">Commercial</h4>
            <div className="flex flex-col gap-3.5 mb-8">
              {[
                { to: "/dealership-fleet", label: "Fleet Services" },
                { to: "/dealership-fleet", label: "Dealership Detailing" },
                { to: "/plans", label: "Maintenance Plans" },
              ].map((link) => (
                <Link key={link.label} to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors link-underline w-fit inline-flex items-center gap-1.5 group">
                  {link.label}
                  <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
            <h4 className="floating-label mb-6">Company</h4>
            <div className="flex flex-col gap-3.5">
              {[
                { to: "/about", label: "About Us" },
                { to: "/reviews", label: "Reviews" },
                { to: "/gallery", label: "Gallery" },
                { to: "/blog", label: "Blog" },
                { to: "/terms", label: "Terms" },
                { to: "/book", label: "Book Now" },
              ].map((link) => (
                <Link key={link.label} to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors link-underline w-fit inline-flex items-center gap-1.5 group">
                  {link.label}
                  <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </nav>
        </StaggerItem>

        <StaggerItem className="md:col-span-2">
          <h4 className="floating-label mb-6">Service Areas — All 13 Cities</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-5">
            {[
              { slug: "vienna-va", label: "Vienna, VA" },
              { slug: "mclean-va", label: "McLean, VA" },
              { slug: "tysons-va", label: "Tysons, VA" },
              { slug: "arlington-va", label: "Arlington, VA" },
              { slug: "alexandria-va", label: "Alexandria, VA" },
              { slug: "fairfax-va", label: "Fairfax, VA" },
              { slug: "falls-church-va", label: "Falls Church, VA" },
              { slug: "great-falls-va", label: "Great Falls, VA" },
              { slug: "reston-va", label: "Reston, VA" },
              { slug: "bethesda-md", label: "Bethesda, MD" },
              { slug: "chevy-chase-md", label: "Chevy Chase, MD" },
              { slug: "silver-spring-md", label: "Silver Spring, MD" },
              { slug: "washington-dc", label: "Washington, DC" },
            ].map((c) => (
              <Link
                key={c.slug}
                to={`/mobile-detailing/${c.slug}`}
                className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5 group"
              >
                <MapPin size={11} className="text-primary group-hover:scale-110 transition-transform" />
                {c.label}
              </Link>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground/70 leading-relaxed mb-5">
            Plus all of Washington DC, Maryland & Northern Virginia.
          </p>
          <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.98 }}>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 gold-gradient-bg text-primary-foreground px-6 py-3 text-sm font-semibold tracking-wide rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 btn-luxury btn-shine"
            >
              Book Now <ArrowRight size={14} />
            </Link>
          </motion.div>
        </StaggerItem>
      </StaggerContainer>

      <div className="glow-line mt-14 mb-8" />

      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-[11px] text-muted-foreground flex items-center gap-1 tracking-wide">
          © {new Date().getFullYear()} Metroshine. Premium Mobile Car Detailing — DC, MD, VA. Made with <Heart size={9} className="text-primary fill-primary" /> All rights reserved.
        </p>
        <div className="flex gap-6">
          {[
            { to: "/terms", label: "Terms & Policies" },
            { to: "/blog", label: "Blog" },
            { to: "/reviews", label: "Reviews" },
            { to: "/book", label: "Book" },
          ].map((link) => (
            <Link key={link.label} to={link.to} className="text-[11px] text-muted-foreground hover:text-primary transition-colors tracking-wide">
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </footer>
));
Footer.displayName = "Footer";

export default Footer;
